import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { View } from 'react-native';

/**
 * Виджет Cloudflare Turnstile — веб-реализация (AUTH-4 / стори 1.6).
 *
 * <p>Скрипт загружается один раз на страницу и остаётся: Turnstile регистрирует глобальный
 * объект, и повторная вставка тега приводит к двойной инициализации и молчащему виджету.
 *
 * <p>Токен живёт недолго и одноразов. Поэтому по `expired-callback` он гасится, а не
 * перевыпускается молча: отправить протухший — значит получить отказ на верно заполненной
 * форме и не понять почему.
 *
 * <p>Внимание при выкатке: скрипт и фрейм Turnstile должны быть разрешены в CSP (SEC-7),
 * иначе виджет не загрузится, а в консоли будет только строка про нарушение политики.
 */
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

type TurnstileApi = {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
    },
  ) => string;
  remove: (id: string) => void;
};

function loadScript(): Promise<TurnstileApi | null> {
  return new Promise((resolve) => {
    const existing = (globalThis as { turnstile?: TurnstileApi }).turnstile;
    if (existing) {
      resolve(existing);
      return;
    }
    const doc = globalThis.document;
    if (!doc) {
      resolve(null);
      return;
    }
    const done = () => resolve((globalThis as { turnstile?: TurnstileApi }).turnstile ?? null);
    const already = doc.getElementById(SCRIPT_ID);
    if (already) {
      already.addEventListener('load', done, { once: true });
      already.addEventListener('error', () => resolve(null), { once: true });
      return;
    }
    const tag = doc.createElement('script');
    tag.id = SCRIPT_ID;
    tag.src = SCRIPT_URL;
    tag.async = true;
    tag.defer = true;
    tag.addEventListener('load', done, { once: true });
    tag.addEventListener('error', () => resolve(null), { once: true });
    doc.head.appendChild(tag);
  });
}

export type CaptchaWidgetProps = {
  siteKey: string;
  /** Токен получен или сброшен (`null`), когда он протух или проверка не удалась. */
  onToken: (token: string | null) => void;
};

export function CaptchaWidget({ siteKey, onToken }: CaptchaWidgetProps) {
  const hostRef = useRef<View | null>(null);
  // Колбэк в ref: Turnstile получает его один раз при render, и пересоздавать виджет на каждый
  // ререндер формы значило бы сбрасывать уже пройденную человеком проверку.
  const onTokenRef = useRef(onToken);
  useLayoutEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    let widgetId: string | null = null;
    let cancelled = false;
    void loadScript().then((api) => {
      const host = hostRef.current as unknown as HTMLElement | null;
      if (cancelled || !api || !host) {
        if (!api) onTokenRef.current(null);
        return;
      }
      widgetId = api.render(host, {
        sitekey: siteKey,
        callback: (token: string) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(null),
        'error-callback': () => onTokenRef.current(null),
        theme: 'auto',
      });
    });
    return () => {
      cancelled = true;
      const api = (globalThis as { turnstile?: TurnstileApi }).turnstile;
      if (api && widgetId) {
        api.remove(widgetId);
      }
    };
  }, [siteKey]);

  return <View ref={hostRef} style={{ minHeight: 68, alignItems: 'center' }} />;
}

export default CaptchaWidget;
